/**
 * Shared types generated from docs/bosun-api-contract.md.
 *
 * Field names and nesting here are BINDING — they are the contract. If a screen
 * needs a field that isn't here, add it to the contract doc first, then to this
 * file, then to the mock server. Never widen an API boundary to `any`.
 */

import type { WireFx } from './api/wire'

/* ============ enums (docs/DATA-MODEL.md) ============ */

export const ITEM_CATEGORIES = ['Provision', 'Marine', 'Engine', 'Safety'] as const
export type ItemCategory = (typeof ITEM_CATEGORIES)[number]

/**
 * The server's real `Item Group` is a populated tree — 1,240 groups, IMPA-style names
 * (`00.PROVISION`, `21. Rope & Hawsers`, …). The former six-value union below matched none of
 * them, so `ItemGroup` is widened to `string` and passes `item_group` through unchanged (see
 * `src/lib/api/mappers/item.ts`).
 */
export const ITEM_GROUPS = ['Galley', 'Cabin', 'Provision', 'Engine', 'Deck', 'Safety'] as const
export type ItemGroup = string

export const ENQUIRY_TYPES = ['Technical', 'Provision', 'Commercial', 'Bonded Stores'] as const
export type EnquiryType = (typeof ENQUIRY_TYPES)[number]

/**
 * Item groups each enquiry type may draw from; `null` = all groups.
 *
 * Built on `ITEM_GROUPS`'s six placeholder values, which do not exist on the real server (see
 * `ItemGroup` above and Q15 in docs/bosun-frappe-open-questions.md). Referenced nowhere outside
 * this file — left as-is rather than removed, since the business question Q15 answers
 * (does a category/group-restriction concept exist at all?) is still open.
 */
export const ENQUIRY_TYPE_GROUPS: Record<EnquiryType, readonly ItemGroup[] | null> = {
  Provision: ['Galley', 'Cabin', 'Provision'],
  Technical: ['Galley', 'Cabin', 'Engine', 'Deck', 'Safety'],
  Commercial: null,
  'Bonded Stores': null,
}

/**
 * IDENTICAL to the server's nine enquiry-status values (Task 7, corrected in fix round 1) —
 * re-verified live 2026-08-04 against `get_pipeline_analytics.by_stage`. No folding of two server
 * statuses onto one board column any more (the old `Quotes Received` + `Comparison Done` → one
 * `Quotes In` column is gone; `Comparison Done` gets its own), and no renaming either — `Won`,
 * `Rejected`, `Archived` are the server's own literal values, not translations of `Lost`/
 * `Cancelled` (that was a different, superseded doctype's enum — see `mappers/status.ts`'s header
 * for the correction).
 */
export const ENQUIRY_STATUSES = [
  'Draft',
  'Open',
  'RFQ Sent',
  'Quotes Received',
  'Comparison Done',
  'Quotation Sent',
  'Won',
  'Rejected',
  'Archived',
] as const
export type EnquiryStatus = (typeof ENQUIRY_STATUSES)[number]

/** `contract` = priced from a blanket order / last purchase, no RFQ. `rfq` = sent to suppliers. */
export type LineRoute = 'contract' | 'rfq'

export const ROLES = ['sales', 'purchaser', 'purchase_manager'] as const
export type Role = (typeof ROLES)[number]

export const UOMS = [
  'pcs', 'no', 'pack', 'box', 'carton', 'pkt', 'bag',
  'kg', 'g', 'L', 'ml', 'set', 'pair', 'drum', 'coil', 'roll',
] as const
export type Uom = (typeof UOMS)[number]

/**
 * Frappe `currency` fields are Links to the Currency doctype, so any ISO code can arrive.
 * The former closed union (AED/USD/EUR/INR) matched only our seed data.
 */
export type Currency = string

/* ============ conventions (contract §1) ============ */

/** Money is ALWAYS an object — never a bare number. Format for display only. */
export interface Money {
  amount: number
  currency: Currency
}

/** List envelope for every collection endpoint. */
export interface ListResponse<T> {
  data: T[]
  total: number
  limit: number
  offset: number
}

/** Error envelope; arrives with 400/403/404/409/422. */
export interface ApiError {
  error: {
    code: string
    message: string
    fields?: Record<string, string>
    /** Present on UOM_NOT_IN_MASTER (contract §6). */
    available?: UomConversion[]
  }
}

/* ============ master data (contract §2, §4) ============ */

export interface UomConversion {
  uom: string
  factor: number
}

export interface SellUom {
  uom: string
  price: Money
}

export interface ItemContract {
  id: string
  supplier: string
  rate: Money
  valid_till?: string | null
}

export interface Item {
  code: string
  name: string
  /**
   * Quoflow has no category concept anywhere in the schema (Q15, still open for the business).
   * Optional rather than defaulted to one of the four values: a guessed category silently
   * mispriced a quotation line's default margin, which `/quote`'s `defaultSellUnit` reads. `/rfq`
   * shows the real `group` instead — see `src/lib/api/mappers/item.ts`.
   */
  category?: ItemCategory
  group: ItemGroup
  base_uom: string
  /** The server's IMPA number for the item — useful for marine item matching. */
  impa_number?: string
  uom_conversions: UomConversion[]
  sell_uoms?: SellUom[]
  /**
   * On-hand stock, when a source for it is known. **OPTIONAL, not defaulted to 0**: Quoflow's
   * `Item` carries no stock quantity at all (see `src/lib/api/mappers/item.ts`), and a defaulted
   * `0` is not "unknown" — it reads as "none in stock", which made `exceedsOnHand` true for every
   * line with a qty and painted a red "exceeds available 0" warning across the whole `/rfq`
   * screen. Undefined means no stock source: no warning, and the badge shows "—".
   */
  on_hand?: number
  base_price?: Money | null
  /** `Supplier.code` values — see `EnquiryItem.suppliers`' own comment. */
  candidate_suppliers: string[]
  contracts: ItemContract[]
  /**
   * On the shelf, INCLUDING stock already promised to other orders. Context for `on_hand`, never
   * a substitute for it — see `on_hand`'s own note.
   */
  on_shelf?: number
  /** Committed to other orders, and therefore not sellable here. */
  reserved?: number
}

export interface Supplier {
  code: string
  name: string
  email: string
  currency?: Currency
}

export interface PurchaseHistoryEntry {
  item: string
  supplier: string
  price: Money
  uom: string
  ref: string
  type: 'SQ' | 'PO'
  date: string
  customer: string
  remark: string
}

/* ============ enquiry / RFQ triage (contract §5) ============ */

export interface EnquiryHeader {
  subject: string
  sender: string
  customer: string
  /** Full legal entity name. */
  company: string
  /** Document-series prefix derived from the company (e.g. "AMS") — read-only. */
  company_series: string
  /** Requesting department; may be empty. */
  department: string
  sales_person: string
  purchaser: string
  enquiry_type: EnquiryType
  use_email_body: boolean
  vessel: string | null
  /** Where the goods are handed over — distinct from the vessel's port of delivery. */
  delivery_location: string | null
  delivery_port: string | null
  /** The customer's own reference for this enquiry (their PO / enquiry number); `''` when unset. */
  customer_reference_no: string
  /** `YYYY-MM-DD`, or `''`. */
  customer_reference_date: string
  /**
   * What the customer will be QUOTED in. `null` when never set — the header card then shows the
   * company's currency as the default, and the first save writes it. Carried into the
   * quotation's `customer_currency` when it is built.
   */
  customer_currency: string | null
  /**
   * The selling price list the delivery location implies (`lookups.get_delivery_locations`
   * carries one per location). Shown as a tag, written when the location changes.
   */
  price_list: string | null
  /** How the enquiry arrived: parsed from mail (`Auto`) or keyed in (`Manual`). */
  type: 'Auto' | 'Manual'
  date: string
  /**
   * The `Communication` this enquiry was created from — real, on-wire (`get_enquiry`'s `mail_id`,
   * confirmed live, see `docs/bosun-required-api-round-2.md` A2's correction), `null` for an
   * enquiry created manually or created before the create-from-mail flow started sending it (see
   * `lib/api/useEnquiryMutations.ts`'s `useCreateEnquiry` header, "`mail_id` (2026-08-05)"). Feeds
   * `SourcePanel` (`features/rfq/SourcePanel.tsx`) — `mail.get_email(mail_id)` returns the body
   * and attachments this enquiry's lines were extracted from.
   */
  mail_id: string | null
}

export interface EnquiryItem {
  /** Sourced from the server's `name`. */
  line_id: string
  /** The customer's own serial number, echoed exactly from their mail. READ-ONLY. Sourced from the server's `enquiry_row_no`. */
  cust_sno: string
  raw_text: string
  /**
   * The line's description as it will print on the RFQ and the quotation. Starts as a copy of
   * `raw_text` (`create_enquiry` sets it so) and is the one field of the two a purchaser may EDIT
   * on the RFQ builder — `raw_text` stays what the customer wrote. `''` when the wire has none.
   */
  description: string
  /**
   * The unit the CUSTOMER asked in, as they wrote it — `Enquiry Item.customer_uom`, added by the
   * Frappe team 2026-09-15 (and, until `B11` is fixed, not yet a database column). Written at
   * create when the customer's unit matched the master, and when the purchaser decides a unit
   * for a line whose customer unit did not (`lib/uomChoice.ts`). `''` when unknown or undecided.
   */
  customer_uom: string
  /**
   * `Enquiry Item.item_creation_req_ref` — the `Item Creation Request` filed for this line, if
   * one was (2026-09-19). Written by the app right after `items.item_creation_request` answers,
   * so the row can say "requested as X" after a reload and the approver can find the line.
   */
  item_creation_req_ref: string | null
  /**
   * Base units per one of `uom`, and `qty × conversion_factor` — the doctype's own two fields,
   * written when the purchaser DECIDES the line's unit (`lib/uomChoice.ts`). The server defaults
   * them to 1 and 0; a stock quantity above 0 is the persisted record of that decision.
   */
  conversion_factor: number
  qty_as_per_stock_uom: number
  item_code: string | null
  confidence: number
  qty: number
  uom: string
  route: LineRoute
  contract_id?: string | null
  /**
   * The rate this line is priced at when it is CONTRACT-routed — the blanket order's own figure,
   * copied verbatim, never derived. `0` when the line has no contracted price.
   *
   * Persists: `Enquiry Item.selected_rate` is a real Currency field and `update_enquiry` stores
   * it (measured 2026-08-29 — a write of `1.5` read back as `1.5`). That is what lets a
   * blanket-order selection survive a reload, since the order's NAME has nowhere to live (an
   * unknown key on the same write is silently dropped). See `lib/blanketOrders.ts`.
   */
  selected_rate: number
  /** `Supplier.code` values (the Link identity) — never display names. This is what RFQ
   * generation and `items.get_supplier_emails` resolve against; resolve to a name at render
   * time only (task-8b-brief.md). */
  suppliers: string[]
  note: string | null
}

export interface Enquiry {
  id: string
  /** The number users read (`AMS-ENQ-…`), falling back to `id`. See `EnquirySummary.ref`. */
  ref: string
  status: EnquiryStatus
  created_at: string
  /**
   * Server-computed (Task 7) — `quoflow.enquiry.get_enquiry`'s own `age_days`, not a client-side
   * `daysSince(status_changed_at)` approximation. Replaces `status_changed_at` entirely; see
   * `src/lib/api/useEnquiries.ts`'s module header for what it's measured from.
   */
  age_days: number
  header: EnquiryHeader
  items: EnquiryItem[]
}

/** Kanban / list row — the enquiry without its lines. */
export interface EnquirySummary {
  id: string
  /**
   * The number USERS read — `AMS-ENQ-2026-002` — as opposed to `id`, which is the Frappe docname
   * (`OPP-2026-00005`) and the only thing safe to route or link with. Falls back to `id` when the
   * server has not assigned an AMS reference. See `displayRef` in `mappers/opportunity.ts`.
   */
  ref: string
  status: EnquiryStatus
  /**
   * The status EXACTLY as the server sent it, before `toBoardStatus` folded anything unrecognised
   * into `Open`. Carried only so the board can tell "genuinely Open" from "a status we don't know",
   * which the fold otherwise makes indistinguishable — see `isKnownBoardStatus`. Never render or
   * route on this; `status` is the value every other consumer wants.
   *
   * Optional because a caller constructing an `EnquirySummary` by hand is making no claim about
   * the server's vocabulary, and must not trip the board's warning by omission.
   */
  status_raw?: string
  /**
   * The `Communication` this enquiry was created from — real and on the LIST row, not only the
   * detail (verified 2026-09-03: 85 of 99 rows carry one; the rest were keyed in by hand).
   *
   * Read to answer "has this mail already been turned into an enquiry?" on the mail screen
   * (`lib/mailProcessed.ts`). `null` means it was NOT created from a mail — never "we don't know".
   */
  mail_id: string | null
  /**
   * Creation timestamp. Genuinely absent from `get_enquiries` — the LIST row has no `creation` and
   * no `created_at` (re-measured 2026-08-09; the detail endpoint has both, so this is a
   * list-payload gap, not a doctype one). `''` here, and nothing should render it: use `date`
   * below, which is real.
   */
  created_at: string
  /**
   * The enquiry's own date — wire `transaction_date`. This is what "how old is this enquiry?"
   * means on the board, and it is the only age signal the list row actually carries.
   *
   * Distinct from `age_days`: this counts from when the enquiry was RAISED, that one from when it
   * last MOVED. A three-week-old enquiry that changed status this morning is `date` = 21 days,
   * `age_days` = 0, and the board needs both — one says how long the customer has been waiting,
   * the other how long this column has been sitting on it.
   */
  date: string
  /** Server-computed (Task 7) — see `Enquiry.age_days`. Days since the last STATUS CHANGE, not
   * since creation: verified against `status_changed_at` on eight live enquiries, 2026-08-09. */
  age_days: number
  /** IDENTITY, never the display label — the `Customer` docname, verbatim. A lookup built on
   * this must fail visibly rather than silently match on a name. See `Quotation.customer`'s own
   * comment for the same rule elsewhere; task 12's fallback-pattern sweep found this field
   * missing it. */
  customer: string
  /** Cosmetic only — falls back to `customer` when the server sends no separate label. Render
   * this, never `customer`, wherever a customer NAME belongs on screen. */
  customer_name: string
  sales_person: string
  purchaser: string
  enquiry_type: EnquiryType
  /** Sourced from the server's `total` (Task 7) — see `src/lib/api/useEnquiries.ts`. */
  value: Money
  blocker: string | null
  /**
   * The enquiry's current quotation, or `null` before one is built. NOT on the list wire —
   * `RequirementsPage` fills it from one `Quotation(name, enquiry)` read (`useQuotationsByEnquiry`)
   * so the board can be searched by quotation number.
   */
  quotation_id: string | null
  /** The vessel CODE on the list row (`Vessel` is named by it), `null` when none. */
  vessel: string | null
}

/** Result of the AI extraction step; appended to the enquiry's items server-side. */
export interface ExtractedItem {
  cust_sno: string
  raw_text: string
  item_code: string | null
  confidence: number
  qty: number
  uom: string
  note?: string | null
}

/** One line of the protected Excel template a supplier was sent. */
export interface RfqItem {
  code: string
  name: string
  qty: number
  uom: string
  /**
   * The STRING through-key back to the enquiry line this RFQ line prices. On the wire all along
   * (`WireRfqItem.enquiry_row_no`) and previously dropped as "no consumer needs it today" — there
   * is one now: filing a supplier quotation from a returned workbook has to carry it, or
   * `get_comparison` cannot attach the offer to its enquiry row and the supplier vanishes from the
   * workbench. Never coerce to a number.
   */
  enquiry_row_no: string
}

/** `sent` until a supplier quotation lands against it — the RFQ's own state. */
export type RfqStatus = 'sent' | 'received'

export interface Rfq {
  id: string
  /** `Supplier.code` — the Link identity. Never compare this to a display name (task-8b). */
  supplier: string
  /** Display name only, when the wire sent one — resolve `supplier` through this for rendering,
   * never the other way around. */
  supplier_name?: string
  item_count: number
  excel_url: string
  email_to: string
  status?: RfqStatus
  /** Timestamp; the "sent N days ago" label is derived from it on the client. */
  sent_at?: string | null
  /** When the supplier was last chased — `reminder_sent_at`, kept apart from `sent_at`. */
  reminded_at?: string | null
  /** The template's lines — present on `GET /enquiries/{id}/rfqs`, absent on create. */
  items?: RfqItem[]
}

export interface GenerateRfqsResponse {
  rfqs: Rfq[]
  enquiry_status: EnquiryStatus
}

/**
 * The covering mail for one supplier's RFQ, as reviewed before sending.
 *
 * `note` is the covering text ONLY. The item table and the protected-Excel link are appended
 * server-side from the RFQ record, so what the supplier reads and what they price cannot
 * drift apart, and no client can mail a quantity the RFQ does not contain.
 */
export interface RfqMessage {
  supplier: string
  to: string[]
  cc: string[]
  subject: string
  note: string
}

/**
 * The OLD `/enquiries/{id}/rfqs` request body — `messages` optional, server defaults fill the
 * rest. Task 8's FE hooks no longer send this (see `SendRfqPayload` below, `rfq.send_rfq`'s real
 * body), but `mock-server/routes/enquiry-writes.ts`'s old route still takes it and this file is
 * re-exported wholesale into `mock-server/contract-types.ts` — kept for that, not for any new
 * caller.
 */
export interface GenerateRfqsPayload {
  messages?: RfqMessage[]
}

/**
 * `rfq.send_rfq`'s request body — EXACTLY these five fields. The server appends the item table
 * (from the RFQ's own snapshot) and the workbook link itself; `note` is covering text only. See
 * `RfqMessage`'s own comment for why that split exists.
 */
export interface SendRfqPayload {
  rfq: string
  to: string[]
  cc: string[]
  subject: string
  note: string
  /**
   * Present on a CHASE (Akhil, 2026-09-16): `send_rfq` re-stamps `sent_at`, so the original is
   * put back and the chase stamped on `reminder_sent_at` afterwards — see `mailRfq`.
   */
  chase?: { original_sent_at: string | null }
}

export interface SendRfqResult {
  id: string
  sent_at: string | null
  sent_to: string[]
  /** When the chase was stamped (`reminder_sent_at`), on a reminder that could be recorded. */
  reminded_at?: string | null
  /** The mail went but the stamp did not — said, so the purchaser knows the record is behind. */
  stamp_warning?: string
  /**
   * Addresses that were NOT reached, when at least one other was.
   *
   * A partial send cannot be reported as a plain failure: the server stamps `sent_at` the moment
   * the FIRST address succeeds, so a caller who treats the whole thing as failed and retries
   * either mails the reached addresses a second time, or is silently blocked for ever by
   * `buildRfqPlan`'s skip-sent rule. The RFQ HAS gone out; these addresses just need chasing by
   * hand. Empty when everything succeeded — and when nothing did, `mailRfq` throws instead.
   */
  failed: Array<{ recipient: string; message: string }>
}

export interface RemindRfqResult {
  reminded_at: string | null
}

/* --- request payloads for the RFQ-builder writes (contract §5, infra C.3) --- */

/**
 * Header fields a user may edit; `PATCH /enquiries/{id}/header` takes any subset.
 * `company_series` is absent by design — it is derived from the company, server-side.
 */
export type EnquiryHeaderPatch = Partial<
  Pick<
    EnquiryHeader,
    | 'subject'
    | 'sender'
    | 'customer'
    | 'company'
    | 'department'
    | 'sales_person'
    | 'enquiry_type'
    | 'use_email_body'
    | 'vessel'
    | 'delivery_location'
    | 'delivery_port'
    | 'type'
    | 'date'
  >
>

/**
 * Line fields a user may edit. `cust_sno` is absent by design — it is the customer's own
 * serial number, echoed from their mail and read-only all the way to the quotation.
 */
export type EnquiryItemPatch = Partial<{
  qty: number
  uom: string
  item_code: string
  /** The printed description — see `EnquiryItem.description`. Persists through `update_enquiry`. */
  description: string
  /** The customer's own unit — see `EnquiryItem.customer_uom`. Written at create only. */
  customer_uom: string
  /** The unit decision — see `EnquiryItem.conversion_factor` / `qty_as_per_stock_uom`. */
  conversion_factor: number
  qty_as_per_stock_uom: number
  /** `Enquiry Item.note` — carries the unit confirmation (`uomConfirmationNote`), 2026-09-18. */
  note: string | null
  /** `Enquiry Item.item_creation_req_ref` — set after an item request is filed (2026-09-19). */
  item_creation_req_ref: string | null
  route: LineRoute
  contract_id: string | null
  /** The contracted rate — see `EnquiryItem.selected_rate`. Written when a blanket order prices
   *  the line; a real field on the wire, so it survives the round trip. */
  selected_rate: number
  /**
   * Confidence in the line's ITEM, 0–1 — written when the matcher chooses one.
   *
   * `Enquiry Item.extraction_confidence` is a real field and round-trips (measured 2026-08-29:
   * `0.03` written, `0.03` read back). Persisting the matcher's score here is what keeps the
   * CONF. column honest after a reload: the matcher is only ever asked about lines that still
   * lack an item, so once a line is matched its score exists nowhere else.
   */
  confidence: number
  /** `Supplier.code` values — see `EnquiryItem.suppliers`' own comment. */
  suppliers: string[]
}>

export interface NewEnquiryItem {
  /**
   * `null` for a line the extraction could not match to the catalogue. Verified live:
   * `create_enquiry` accepts such a row and keeps its `raw_text`, so an unmatched line now
   * reaches the RFQ builder for triage instead of being dropped client-side.
   */
  item_code: string | null
  qty: number
  /** `null` when the UOM could not be verified against the `UOM` master — a Frappe Link, so an
   * unverified string fails the whole document rather than just its row. */
  uom: string | null
  /** The customer's original text for this line. Carried so an unmatched line arrives with
   * something the purchaser can act on. */
  /** The unit the customer asked in, as written — recorded for next time (`customer_uom`). */
  customer_uom?: string
  raw_text?: string
  description?: string
  /**
   * The line number this line takes (2026-09-22) — the customer's own serial, so a line the
   * reader missed goes in as line 3, not line 8. Lines from that number on move down one.
   * Absent: the next free number.
   */
  at?: string
  /** The model's own confidence for this line, 0–1. Sent so the RFQ builder's CONF. column means
   * something on a freshly created enquiry — without it every line reads 0%. */
  confidence?: number
  /** `Supplier.code` values — see `EnquiryItem.suppliers`' own comment. */
  suppliers?: string[]
}

export interface AssignSuppliersPayload {
  line_ids: string[]
  /** `Supplier.code` values — see `EnquiryItem.suppliers`' own comment. */
  suppliers: string[]
  /** `add` (default) unions; `remove` takes the named ones off; `clear` empties the lines. */
  mode?: 'add' | 'remove' | 'clear'
}

export interface MatchLinesPayload {
  line_ids: string[]
  item_code: string
  /**
   * The unit each line should end up with, keyed by `line_id` — a line is only present when its
   * unit has to CHANGE.
   *
   * Per line, not one value for the batch, even though every line is being matched to the same
   * item: each carries its own customer wording and its own current unit, so
   * `uomAfterItemChange` can legitimately answer differently for two rows pointed at one item.
   *
   * Sent in the same wholesale write as `item_code`, so correcting the unit costs no extra round
   * trip. Omit it entirely and the units are left alone — which is what left a bulk-matched line
   * reading "KG not in master" (see `packSize.ts`'s `uomAfterItemChange`).
   */
  uom_by_line?: Record<string, string>
  /**
   * The item's own suppliers, applied to every line in the batch.
   *
   * Same reasoning as the single-line path (`RfqPage`'s `applyItemToLine`): the master already
   * knows which suppliers carry this item, and ticking them by hand on each of sixty lines is the
   * screen's biggest cost. Omitted when the master knows none — an empty array would CLEAR
   * whatever a purchaser had already chosen, because `suppliers` replaces wholesale.
   */
  suppliers?: string[]
}

export interface ExtractResponse {
  items: ExtractedItem[]
}

/* ============ supplier quotations (contract §7) ============ */

export interface SupplierQuotationItem {
  item_code: string
  rfq_uom: string
  sq_uom: string
  uom_mismatch: boolean
  /** The rate as the supplier quoted it, in the supplier's own currency. */
  rate: Money
  /**
   * The same rate in the company's base currency, as computed BY THE SERVER
   * (`Supplier Quotation Item.base_rate`). Present only on documents read through the OLD
   * Frappe `get_document` transport (`mappers/supplierQuotation.ts`, still used by
   * `useComparison.ts` until Task 10 migrates it) — the new `supplier_quotation.
   * get_supplier_quotations`/`get_supplier_quotation` methods (`useSupplierQuotations.ts`,
   * Task 9) never populate this field; see `rate_in_customer_currency` below for what THAT
   * transport sends instead. Prefer whichever one your transport actually returns over
   * multiplying `rate` by an FX rate: the server applied the document's own `conversion_rate`
   * and rounding, and a client-side product will drift from the totals the server persisted.
   */
  base_rate?: Money
  /**
   * The rate in the CUSTOMER's currency, as computed by the server (`Supplier Quotation Item.
   * rate_in_customer_currency`) — populated only by the new `supplier_quotation.*` methods
   * (Task 9). Prefer it over a client-side FX product for the same reason `base_rate` says.
   *
   * GAP: `get_supplier_quotations`/`get_supplier_quotation`'s verified response carries no
   * `customer_currency` field to label this amount with — the doctype has one
   * (`Supplier Quotation.customer_currency`), but this method doesn't send it. Until it does,
   * `useSupplierQuotations.ts` labels this `Money` with the app-wide base currency (`getBaseCurrency()`) as the closest
   * honest stand-in (most enquiries in this system settle in it), NOT a confirmed customer
   * currency — flagged in task-9-report.md for the backend follow-up list. Do not trust
   * `.currency` on this field until that's resolved.
   */
  rate_in_customer_currency?: Money
  stock: number
  remark: string
  /** The STRING through-key back to the enquiry line this offer prices — never coerce to a
   * number. `''` when the transport carries none (the OLD Frappe mapper doesn't send it). */
  enquiry_row_no: string
  /** `Supplier Quotation Item.opportunity_item` — the Link to the specific `Opportunity Item`
   * row, when the transport sends one. `''` when it doesn't. */
  opportunity_item: string
}

export interface SupplierQuotation {
  id: string
  /** The RFQ this reply answers. */
  rfq: string | null
  /** `Supplier.code` — the Link identity (task 9). Never a display name; resolve one through
   * the paired `Rfq.supplier_name` when rendering (see `lib/sq.ts`'s `pairRepliesToRfqs`). */
  supplier: string
  /** The currency the supplier quoted in; individual rates still carry their own. */
  currency: Currency
  status: 'pending' | 'received'
  received_at: string | null
  items: SupplierQuotationItem[]
  /** The document's own persisted total, in `currency` — present only when the transport sends
   * one (the new `supplier_quotation.*` methods do; the OLD Frappe mapper doesn't ask for it). */
  grand_total?: Money
}

/** `supplier_quotation.create_supplier_quotation`'s one line — a bare `rate` number, currency on
 * the parent payload (contract §1's money convention applies to writes too). */
export interface CreateSupplierQuotationItemPayload {
  item_code: string
  qty: number
  uom: string
  rate: number
  /**
   * The enquiry line this prices. WITHOUT IT the quotation is filed successfully and then cannot
   * be seen: `comparison.get_comparison` joins offers to enquiry rows on this key, so a line
   * missing it is dropped from `items[].offers` while the supplier still appears in `suppliers[]`
   * — measured 2026-08-08 on SQ-2026-00010, which showed one supplier on a two-supplier enquiry.
   */
  enquiry_row_no?: string
  /** The `Opportunity Item` docname, the same through-key by another route — our generated
   *  workbook carries it in its own column, so a returned sheet hands it straight back. */
  opportunity_item?: string
  /** The RFQ's own UOM for this line, when the caller already knows it (e.g. keying in a reply
   * by hand against a visible RFQ). Left to the server to fill in from the RFQ otherwise. */
  rfq_uom?: string
  remark?: string
  /**
   * The enquiry line's printed description — the purchaser's edit, else the customer's wording,
   * never our notes (2026-10-16). Accepted by the method (the collection's full body carries it);
   * left out, ERPNext fills the row from the Item master and "general paint" written over a master
   * name never reaches the quotation.
   */
  description?: string
}

/** `supplier_quotation.create_supplier_quotation`'s `data` body — a quotation entered by hand
 * (a phone call, a reply the parser couldn't handle) rather than parsed from mail. */
export interface CreateSupplierQuotationPayload {
  rfq: string
  enquiry: string
  /** `Supplier.code`. */
  supplier: string
  transaction_date: string
  currency: string
  company: string
  items: CreateSupplierQuotationItemPayload[]
}

/** `supplier_quotation.parse_supplier_reply`'s body — an inbound supplier mail to parse against
 * the RFQ it answers. Field names match the wire body exactly (`{rfq_id, communication}`). */
export interface ParseSupplierReplyPayload {
  rfq_id: string
  /** The `Communication`/mail id being parsed. */
  communication: string
}

/**
 * What the parser actually did — surfaced to the user in full, never just the happy path.
 * `unmatched` above zero means lines the supplier priced that the parser could not tie back to
 * an enquiry item; silently dropping them would lose part of a real supplier quote.
 */
export interface ParseSupplierReplyResult {
  supplier_quotation: string | null
  matched: number
  unmatched: number
  uom_warnings: string[]
}

/* ============ comparison (contract §8) — API gives facts, FE does the math ============ */

export interface Offer {
  /**
   * `Supplier.code` — identity, carried ON the offer rather than implied by a map key.
   *
   * It used to be the key of `ComparisonItem.offers`, which meant one offer per supplier. That is
   * not what the server has: measured on ENQ-2026-00018 (2026-08-08), `get_comparison` listed FIVE
   * `Supplier Quotation`s — SQ-2026-00001/-00002/-00003/-00004/-00006 — all under the SAME supplier
   * code, and the map could only reach two of them. Three quotations were unreachable and could
   * never win an allocation.
   */
  supplier: string
  /**
   * The `Supplier Quotation` this offer came from. THE identity of an offer: one supplier may quote
   * the same line several times (a revision, a second branch, a re-quote), and only this tells them
   * apart.
   */
  supplier_quotation: string
  rate: Money
  stock: number
  remark: string
  /**
   * The rate already converted to base by the SERVER, when it sends one. Preferred over converting
   * `rate` through the FX snapshot, because it is what the server will price the quotation with —
   * `null` when the wire carries no converted figure and the client must do the conversion itself.
   */
  rate_in_base: Money | null
  /** `"YYYY-MM-DD"`, or `''`. Promised delivery, shown but never ranked on. */
  delivery_date: string
  /** The UOM the RFQ asked for. */
  rfq_uom: string
  /** The UOM the supplier actually quoted in — `rate` is per ONE of these. */
  sq_uom: string
  /**
   * A real server field (`Supplier Quotation Item.uom_mismatch`), arriving as `0|1` and
   * normalized by `bool01`. The workbench must convert with `item.uom_conversions` before
   * ranking; an unconvertible offer is excluded, never compared on the raw number.
   */
  uom_mismatch: boolean
}

/**
 * One customer line behind a comparison row. Several appear when the enquiry asked for the
 * same item more than once — the row is the sum, these are what the customer actually wrote.
 */
export interface ComparisonLine {
  line_id: string
  /** The customer's own serial number for the line — their reference, not ours. */
  cust_sno: string
  /** The customer's original wording, before it was matched to the item master. */
  description: string
  /** The purchaser's edited description, when it differs from the customer's text. */
  edited?: string
  qty: number
}

export interface ComparisonItem {
  item_code: string
  name: string
  qty: number
  /** The unit the enquiry line is in; every offer's `rfq_uom` on this row. */
  uom: string
  /**
   * The STRING through-key back to the enquiry line this row prices (never coerce to a
   * number). `comparison.get_comparison` sends one row per ENQUIRY LINE, not per item code —
   * a customer who asked for the same item on two lines gets two independent
   * `ComparisonItem`s, each with its own `enquiry_row_no`, qty and offers, never merged into
   * one. `AllocationRow` keys on `(item_code, enquiry_row_no, supplier)` for exactly this
   * reason — `item_code` alone cannot tell two such rows apart.
   */
  enquiry_row_no: string
  /** Always one entry today (see `enquiry_row_no` above) — kept as a list rather than a single
   * field since the row-level display (customer's own wording, `cust_sno` pill) is naturally
   * per-line, not per-item. */
  lines: ComparisonLine[]
  /**
   * Every offer against this line, one per `Supplier Quotation` — a LIST, not a map keyed by
   * supplier.
   *
   * A supplier-keyed map cannot hold two quotations from one supplier, and the live server sends
   * exactly that (see `Offer.supplier`). Suppliers that did not quote this line simply have no
   * entry, rather than a `null` one.
   */
  offers: Offer[]
}

/** `comparison.get_comparison`'s whole payload — contract §8. */
export interface Comparison {
  enquiry: string
  fx: WireFx
  suppliers: ComparisonSupplier[]
  items: ComparisonItem[]
  /** The last saved split, if any — seeds the workbench so a reload doesn't lose it. */
  allocations: AllocationRow[]
}

/** A supplier column in the comparison matrix. */
export interface ComparisonSupplier {
  supplier: string
  supplier_name: string
  /** The currency this supplier quoted in — every rate in their column is in it. */
  currency: Currency
  /** The supplier quotation the column was built from. */
  sq: string
}

/**
 * One supplier's share of one item. Several rows may share an `item_code` — that is the
 * multi-supplier split, and it is why this is a flat list rather than a map.
 */
export interface AllocationRow {
  item_code: string
  enquiry_row_no: string
  supplier: string
  /** In RFQ UOM — a slice of the row's `qty`, never the supplier's quoted unit. */
  allocated_qty: number
  /** Per ONE unit of RFQ UOM, in `currency` — NOT per the supplier's quoted unit. A supplier
   * quoting AED 24/box of 24 pcs against a pcs-denominated RFQ line stores `rate: 1`, so
   * `rate × allocated_qty` is correct without the reader needing to know the pack factor (this
   * row carries no `uom` field to disambiguate otherwise). See `lib/compare.ts`'s `makeRow`. */
  rate: number
  /** The supplier's own quoted currency — Akhil's `po_currency`. */
  currency: Currency
  /**
   * The supplier's OWN quoted rate, verbatim — Akhil's `po_currency_rate` ("we just need to pass
   * what is given by the supplier").
   *
   * Distinct from `rate` above, which is that figure restated into the RFQ's unit for costing.
   * They differ exactly when the supplier quoted in another unit: 288 per carton against a
   * per-piece line gives `rate: 12` and `quoted_rate: 288`. Absent when there was nothing to
   * convert, in which case `rate` is already the supplier's figure.
   */
  quoted_rate?: number
  /**
   * What `quoted_rate` is in — set when the row was restated into the company's currency by
   * `allocationCurrency.ts`, so the PO side of the quotation still names the supplier's currency
   * after `currency` above has become the company's. Absent means `currency` is the supplier's.
   */
  quoted_currency?: Currency
  /**
   * The `Supplier Quotation` the chosen offer came from — Akhil's `supplier_quotation`.
   *
   * Carried on the ROW, not looked up from the comparison's supplier column: one supplier may
   * quote the same line several times (a revision, a second branch, a re-quote) and only this
   * tells them apart — see `Offer.supplier_quotation`. `''` when unknown.
   */
  supplier_quotation?: string
  /** What the supplier said about this offer — Akhil's `offer_remarks`. `''` when they said nothing. */
  remark?: string
}

/* ============ quotation (contract §9) ============ */

export interface QuotationLine {
  line_id: string
  cust_sno: string
  item_code: string
  /** `provision` = contract-priced line; `rfq` = came through the comparison. */
  tag: 'provision' | 'rfq'
  source: string
  qty: number
  uom: string
  sell_uom?: string | null
  cost_unit: Money
  /**
   * Quoflow has no category concept (Q15, still open for the business) — see `Item.category`
   * in this file and `src/lib/api/mappers/item.ts`. Optional rather than defaulted: the server's
   * own per-line `margin` (`QuotationLine.default_margin_pct`) already supplies the default sell
   * price, so a category is no longer needed to price a line and a guessed one would only mislead.
   */
  category?: ItemCategory
  default_margin_pct: number
  sell_unit: Money
  /** The catalogue name as stored on the line — what the customer's copy prints. */
  item_name: string
  /** Printed description, editable on the quote page (Akhil, 2026-09-14). `''` when none. */
  description: string
  /** Printed remark against the line — "ex-stock", "subject to availability". `''` when none. */
  offer_remarks: string
  /** The supplier's currency — what the PO will be raised in. */
  po_currency: string
  /** The supplier's own unit rate in `po_currency`. NOT an exchange rate. */
  po_currency_rate: number
  /** `po_currency` → the quotation's currency; `po_currency_rate × conversion_rate = cost_unit`. */
  conversion_rate: number
}

export interface QuotationTotals {
  cost_base: Money
  sell_subtotal: Money
  commission_pct: number
  /**
   * The top-level uplift the SERVER multiplies the subtotal by — `Quotation.markup`. Carried on
   * the totals so the panel displays the same number `previewTotals` applied; a panel reading it
   * from somewhere else is how the two drift.
   */
  markup_pct: number
  convenience: Money
  total: Money
  margin_pct: number
}

/**
 * The commission panel and discount block on a quotation — Akhil's ERP screen, 2026-09-04.
 *
 * Every field is real on the live `Quotation` doctype. `total_commission` is SERVER-COMPUTED from
 * the four rates, so it is read and never written; `lib/quotationCommission.ts` derives the same
 * figure for display while an edit is still in flight.
 */
/** Exactly the `charge_type` Select's values on the live doctype. */
export type ChargeType =
  | 'Actual'
  | 'On Net Total'
  | 'On Previous Row Amount'
  | 'On Previous Row Total'
  | 'On Item Quantity'

export interface QuotationCommission {
  management: number
  software: number
  port_agent: number
  other_charges: number
  /** As the server computed it. May lag a pending edit — the panel previews its own sum. */
  total_commission: number
  /** The per-line margin the panel applies to every unlocked line. */
  markup: number
  /** The total commission percentage the line prices were grossed up for. Stored on
   *  `Quotation.line_commission`, which is inert — see `useQuotation.ts`'s `WireQuotation`. */
  line_commission?: number
}

/**
 * One row of `Quotation.taxes` (`Sales Taxes and Charges`), as the screen holds it.
 *
 * `id` is the child docname when the row came from the server, and a synthesised key for a row the
 * purchaser has just added — React needs a stable key before the server has given one, and the
 * `idx` alone is not stable while rows are being reordered or removed.
 */
export interface QuotationTaxRow {
  id: string
  idx: number
  charge_type: ChargeType
  account_head: string
  tax_rate: number
  /** Typed for `Actual`; computed for every other charge type. */
  tax_amount: number
  /** The `idx` of the row a "Previous Row" charge refers to. */
  row_id: string
  /** Running total after this row — stored, so the printed document can show it. */
  total: number
}

export interface QuotationDiscount {
  /** Which total the percentage is taken against. The doctype's Select allows exactly these. */
  apply_on: 'Grand Total' | 'Net Total'
  /**
   * Whether `apply_on` is actually on the document, or only the ERP's default shown in its
   * place. A new quotation stores `''`; the panel says so rather than pretending it is set.
   */
  apply_on_stored: boolean
  pct: number
  amount: number
}

export interface Quotation {
  id: string
  status: 'draft' | 'Quoted' | 'Won' | 'Rejected' | 'Archived'
  /** The Customer doctype's own docname — IDENTITY, for a lookup keyed on the customer (e.g.
   * `/customers/{customer}/quoted-items`). Never a display label, and never falls back to one:
   * a lookup built on this must fail visibly rather than silently query the wrong customer. */
  customer: string
  /** Cosmetic only — falls back to `customer` when the server sends no separate label, same
   * pattern `ComparisonSupplier.supplier_name` uses. Never read for identity. */
  customer_name: string
  grouped: boolean
  revision?: number
  lines: QuotationLine[]
  totals: QuotationTotals
  /** Snapshot for display conversion into the customer currency (contract §9). */
  fx: WireFx
  /** The currency every line's cost AND sell are in — `customer_currency`, else the company's. */
  currency: Currency
  /** Company currency → `currency`, as stored on the document. `1` for a same-currency quotation. */
  exchange_rate: number
  /** The commission panel's five percentages, read off the document. */
  commission: QuotationCommission
  /** The additional-discount block. */
  discount: QuotationDiscount
  /** `Σ(cost × qty)` as the SERVER stores it (`total_po_cost`); the panel recomputes for preview. */
  total_po_cost: number
  /** The discount VALUE in the company's currency — `Quotation.base_discount_amount`, writable. */
  base_discount_amount: number
  /**
   * The totals as the SERVER stored them, verbatim. `totals` above is what the page derives and
   * shows; these are what the document carries, so `lib/derivedFigures.ts` can tell when the two
   * disagree and write ours back (the server recomputes some of them from the lines alone).
   */
  stored_totals: {
    net_total: number
    total: number
    grand_total: number
    total_commission: number
    quotation_markup: number
    overall_margin: number
    total_taxes_and_charges: number
    base_total: number
    base_net_total: number
    base_grand_total: number
    base_total_taxes_and_charges: number
  }
  /** The supplier-side currency the PO figures are denominated in. */
  po_currency: string
  /** `Sales Taxes and Charges` — see `lib/salesTaxes.ts` for what each charge type computes. */
  taxes: QuotationTaxRow[]
  /** The `Terms and Conditions` template chosen, if any — a Link, so a docname or `''`. */
  tc_name: string
  /** The `Payment Terms Template` docname; `''` when none chosen. */
  payment_terms_template: string
  /** The `Address` docname the quotation is addressed to; `''` when none. */
  customer_address: string
  /** Who handles it — the customer's sales person and the purchaser running the enquiry. */
  sales_person: string
  purchaser: string
  /** The customer's own reference (their PO / enquiry number), quoted back to them. */
  customer_reference_no: string
  /** Free text — "3 days", "on arrival". The doctype spells the field `delivey_time`. */
  delivery_time: string
  /** The company the quotation is issued by — the enquiry's; `''` on a document from before it was mandatory. */
  company: string
  /** Sales · Service · Maintenance · Shopping Cart (Akhil, 2026-09-17: editable on the quotation). */
  order_type: string
  /** The quotation's own enquiry type — a Link, mandatory since 2026-09-17 (pm); `''` on older documents. */
  enquiry_type: string
  /** Logistics — the `Vessel` code, `Port` and `Delivery Location` docnames; `''` when unset. */
  vessel: string
  port: string
  delivery_location: string
  /** The terms themselves, as HTML. Seeded from the template but editable per quotation. */
  terms: string
}

export interface RuleViolation {
  line_id: string
  reason: string
}

export interface ValidateResponse {
  ok: boolean
  violations: RuleViolation[]
}

/** What the CUSTOMER asked for (contract §9). */
export const REVISION_TYPES = ['add', 'set_qty', 'reprice', 'remove'] as const
export type RevisionType = (typeof REVISION_TYPES)[number]

/**
 * One outstanding customer request against a sent quotation.
 *
 * `quotation.get_revisions` (Task 11, live-verified) carries no stable per-request id — only
 * `revision_no` (the round the request was raised in). `id` here is therefore SYNTHESIZED
 * client-side (`useRevisions.ts`'s `toRevisionRequest`) for React keys and UI selection only; it
 * is never sent back. `apply_revisions` resolves a change against the quotation by `item_code`,
 * not by closing a specific request id — see `RevisionChange` below.
 */
export interface RevisionRequest {
  id: string
  revision_no: number
  type: RevisionType
  item_code: string | null
  qty: number | null
  detail: string
  status: 'open' | 'done'
  raised_on: string
}

export interface RevisionsResponse {
  /** The quotation's current round. */
  revision: number
  requests: RevisionRequest[]
}

/**
 * What we DO about a request — `quotation.apply_revisions`'s `changes[]` (Task 11,
 * live-verified: `{op, item_code, qty, uom?}`). Narrower than the pre-migration shape: no
 * `line_id` (targets by `item_code` instead), no `request_id` (the server has no way to be told
 * which request a change closes — see `RevisionRequest`'s header), and no `revise_rate` op at
 * all. A "reprice" customer request is resolved through `useUpdateQuotationLine` instead (the
 * line's own `sell_unit`/`margin`), not through this endpoint — this wire has no field for a
 * price anywhere in it.
 */
export const REVISION_OPS = ['add', 'set_qty', 'remove'] as const
export type RevisionOp = (typeof REVISION_OPS)[number]

export interface RevisionChange {
  op: RevisionOp
  item_code: string
  qty: number
  uom?: string
}

/** `POST /quotations/{id}/status` (contract §9). */
export interface DecideResponse {
  id?: string
  status: EnquiryStatus
  /** A Won also pushes the enquiry chain to G6 (2026-10-16). Set when that push failed — the
   *  decision itself stood. See `lib/api/g6Push.ts`. */
  push_warning?: string
}

export interface ApplyRevisionsResponse {
  revision: number
  status: Quotation['status']
}

export interface QuotedItem {
  item_code: string
  name: string
  supplier: string
  last_price: Money
  uom_conversions: UomConversion[]
  ref: string
  type: 'SQ' | 'PO'
  date: string
  remark: string
}

/* ============ settings (contract §10) ============ */

export type PricingRule =
  | { id: string; kind: 'noBelowCost' }
  | { id: string; kind: 'minMarkup'; pct: number }
  | { id: string; kind: 'minMarkupCat'; cat: ItemCategory; pct: number }
  | { id: string; kind: 'roundSell'; step: number }

/**
 * `settings.get_pricing_rules` (Task 11, live-verified) bundles what used to be three separate
 * `/api/v1` endpoints (`/settings/pricing-rules`, `/settings/category-margins`, and
 * `/settings/company`'s `default_markup_pct`/`base_currency`) into one response. The three extra
 * fields are OPTIONAL rather than merged into a required shape so the OLD, still-typechecked
 * dead `/api/v1` mock route (`mock-server/routes/misc.ts` — no caller since this task, deleted in
 * Task 12) kept constructing a bare `{enforce, rules}` without needing a matching update. (NOT
 * `quotations.ts` — that file has one live caller left, `GET /customers/:id/quoted-items`
 * (`useQuotedItems`), so it survives Task 12; task-11-report.md's claim that it was fully dead was
 * wrong for that one route — see task-12-report.md.)
 */
export interface PricingRulesSettings {
  enforce: boolean
  rules: PricingRule[]
  category_margins?: CategoryMargins
  default_markup_pct?: number
  /** `null` on the live bench (defect B5) — `useSettings.ts`'s `usePricingRules` always resolves
   * this to a real value (falling back to `FALLBACK_BASE_CURRENCY`), so a caller reading it off a
   * hook result never sees `undefined`; it stays optional here only for the dead-route reason above. */
  base_currency?: Currency
}

export type CategoryMargins = Record<ItemCategory, number>

/**
 * NOT on the new transport — `settings.get_pricing_rules` has no company/mailbox endpoint
 * equivalent (Task 11). `commission_pct`/`convenience` now live PER-DOCUMENT on `Quotation`
 * (`management_commission`/`convenience_amount` on the wire), and `default_markup_pct`/
 * `base_currency` moved onto `PricingRulesSettings` above. Kept only so `mock-server/routes/
 * quotations.ts` (still alive for `GET /customers/:id/quoted-items`, `useQuotedItems` — see
 * `PricingRulesSettings`'s own comment above) still typechecks — no live hook constructs or
 * reads this type any more.
 */
export interface CompanySettings {
  base_currency: Currency
  default_markup_pct: number
  commission_pct: number
  convenience: Money
  mailboxes: { customer: string; supplier: string }
}

/* ============ mail (contract §11) ============ */

export type MailAccount = 'customer' | 'supplier'
export type MailFolder = 'inbox' | 'flagged' | 'drafts' | 'sent' | 'deleted' | 'archive'

/**
 * A row in the folder rail. The two inboxes belong to an account; Flagged/Drafts/Sent/Deleted
 * are shared across both (`account: null`).
 */
export interface MailFolderSummary {
  key: MailFolder
  title: string
  account: MailAccount | null
  /**
   * Unread count — the OPEN folder counts its own loaded rows, any other uses the server's
   * `folder_counts`.
   *
   * `undefined` means genuinely unknown, and the rail then renders no badge. It used to be a
   * required `number` filled from `total`, which for an unopened folder is the row count of a
   * `limit: 1` fetch — so every folder permanently displayed "1", a number describing our own
   * request rather than the mailbox. A zero would have been just as wrong: it claims "nothing
   * unread" about a folder nobody counted.
   */
  unread?: number
  total: number
}

/** An uploaded file, referenced by id when composing (contract §11). */
export interface UploadedFile {
  id: string
  name: string
  kind: MailAttachment['kind']
  size: string
  preview_url?: string | null
}

/** `POST /mail/send` and `POST /mail/drafts` take the same body. */
export interface ComposePayload {
  account: MailAccount
  to: string[]
  cc?: string[]
  subject: string
  body_html: string
  /** Ids from `POST /files`. */
  attachments?: string[]
  in_reply_to?: string
}

export interface SendMailResponse {
  id: string
  folder: MailFolder
  sent_at?: string
}

/** `POST /mail/{id}/start-rfq` — the mail screen's main action (contract §11). */
export interface StartRfqPayload {
  attachment_id?: string
  use_body?: boolean
}

export interface StartRfqResponse {
  enquiry_id: string
  /** True when the mail's `[REQ-####]` tag matched nothing and a new enquiry was made. */
  created: boolean
  item_count: number
}

export interface MailAttachment {
  id: string
  name: string
  kind: 'xlsx' | 'pdf' | 'doc' | 'img' | 'file'
  size: string
  preview_url?: string | null
}

export interface MailMessage {
  id: string
  account: MailAccount
  folder: MailFolder
  from: string
  from_name: string
  /** Recipients — needed to re-open a draft and to reply-all. */
  to: string[]
  cc: string[]
  subject: string
  preview: string
  /** Present when the message was opened individually; omitted from list rows. */
  body_html: string
  received_at: string
  unread: boolean
  flagged: boolean
  /** The `[REQ-####]` subject tag that links the mail to an enquiry. */
  enquiry_id: string | null
  attachments: MailAttachment[]
}

/* ============ analytics (contract §12) — backend aggregates, FE renders ============ */

export interface Blocker {
  id: string
  customer: string
  reason: string
  age_days: number
  owner: string
}

export interface PurchaserStat {
  name: string
  handled: number
  won: number
  win_rate: number
  /** Not on `analytics.get_pipeline_analytics` (Task 11, live-verified: `{name, handled, won,
   * win_rate}`) — optional so the dead `/api/v1` `mock-server/routes/misc.ts` (no caller since
   * this task) can keep supplying it without a matching type change. */
  avg_response_days?: number
}

export interface PipelineAnalytics {
  by_stage: Record<string, number>
  conversion_rate: number
  avg_cycle_days: number
  win_rate: number
  /** Real total on the new wire; optional only so the OLD payload shape (which never had it)
   * keeps typechecking — see `PurchaserStat.avg_response_days`'s comment. */
  total_enquiries?: number
  /** STUB on both transports (no status-history table anywhere to average from) — always `{}`
   * from `usePipelineAnalytics`, never on the new wire at all. Optional for the same dead-route
   * reason as the other two fields here. */
  stage_avg_days?: Record<string, number>
  blockers: Blocker[]
  per_purchaser: PurchaserStat[]
}

/* ============ realtime (docs/bosun-email-api-infra.md) ============ */

export type RealtimeEvent =
  | { event: 'mail.new'; account: MailAccount; mail_id: string }
  | { event: 'sq.received'; enquiry_id: string; sq_id: string; supplier: string }
  | { event: 'extract.done'; enquiry_id: string; item_count: number }
  | { event: 'enquiry.status'; enquiry_id: string; status: EnquiryStatus }
